import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/shadcn/dialog";
import { Button } from "@/components/shadcn/button";
import type { ContactNote } from "@/types/domain";

type NoteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  note?: ContactNote | null;
  onSave: (subject: string, body: string) => Promise<void>;
};

export const NoteDialog = ({ open, onOpenChange, note, onSave }: NoteDialogProps) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(note?.subject ?? "");
      setBody(note?.body ?? "");
      setSubmitAttempted(false);
    }
  }, [open, note]);

  const handleSave = async () => {
    setSubmitAttempted(true);
    if (!subject.trim()) return;
    setSaving(true);
    try {
      await onSave(subject, body);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const subjectInvalid = submitAttempted && !subject.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{note ? "Edit Note" : "Add Note"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`mt-1 w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${subjectInvalid ? "border-red-500 bg-red-50" : "border-gray-300"}`}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
            />
            {subjectInvalid && (
              <p className="text-xs text-red-500 mt-0.5">Subject is required</p>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Body</label>
            <textarea
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Note text..."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" className="bg-kPurple-500 hover:bg-kPurple-700 text-white hover:text-white font-bold" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
